import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, Check, Minus, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";

const PERIODS = [
  { key: "monthly", label: "Monthly", subtitle: "Goals for this month", accent: "#0066ff" },
  { key: "weekly",  label: "Weekly",  subtitle: "Goals for this week",  accent: "#7C3AED" },
];

export default function Deliverables() {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {mode:"create",period} | item

  const load = async () => {
    const { data } = await api.get("/deliverables");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const grouped = (period) => items.filter((d) => d.period === period);

  return (
    <div data-testid="deliverables-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Goals</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Monthly Deliverables</h1>
          <p className="mt-2 text-sm text-zinc-600">Recurring goals with positive reinforcement. Check things off and celebrate.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {PERIODS.map((p) => {
          const list = grouped(p.key);
          const doneCount = list.filter((d) => d.completed).length;
          return (
            <div key={p.key} data-testid={`deliverables-${p.key}-section`} className="rounded-2xl border border-zinc-200 bg-white p-5">
              <div className="flex items-start justify-between gap-3 border-b border-zinc-100 pb-3" style={{ borderTopColor: p.accent, borderTopWidth: 3, borderTopStyle: "solid", marginTop: -20, paddingTop: 20 }}>
                <div>
                  <div className="text-base font-semibold tracking-tight" style={{ color: p.accent }}>{p.label}</div>
                  <div className="mt-0.5 text-[11px] text-zinc-500">
                    {p.subtitle}{list.length > 0 && <> · <strong className="text-zinc-700">{doneCount}/{list.length}</strong> done</>}
                  </div>
                </div>
                {!readOnly && (
                  <button
                    data-testid={`deliverables-${p.key}-add`}
                    onClick={() => setEditing({ mode: "create", period: p.key })}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0052cc]"
                  >
                    <Plus size={12} /> Add
                  </button>
                )}
              </div>
              <div className="mt-4 flex flex-col gap-2">
                {list.map((d) => (
                  <DeliverableCard
                    key={d.id}
                    item={d}
                    readOnly={readOnly}
                    accent={p.accent}
                    onEdit={() => setEditing(d)}
                    onReload={load}
                  />
                ))}
                {list.length === 0 && (
                  <div className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-center text-xs text-zinc-400">
                    No {p.label.toLowerCase()} goals yet. {!readOnly && "Add one to get going."}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <DeliverableModal
          initial={editing.mode === "create" ? null : editing}
          defaultPeriod={editing.period || editing?.period}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DeliverableCard({ item, readOnly, accent, onEdit, onReload }) {
  const [celebrate, setCelebrate] = useState(false);
  const counterTarget = item.counter_target || 0;
  const counterValue = item.counter_value || 0;
  const counterDone = item.counter_enabled && counterTarget > 0 && counterValue >= counterTarget;

  const setCounter = async (delta) => {
    if (readOnly) return;
    const newVal = Math.max(0, counterValue + delta);
    try {
      await api.put(`/deliverables/${item.id}`, { counter_value: newVal });
      // Auto-complete when counter hits target
      if (item.counter_target && newVal >= item.counter_target && !item.completed) {
        await markComplete(true, true);
      } else {
        onReload();
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const markComplete = async (completed, silent = false) => {
    if (readOnly) return;
    try {
      await api.put(`/deliverables/${item.id}`, { completed });
      if (completed && !silent) {
        triggerCelebrate();
      } else if (completed && silent) {
        triggerCelebrate();
      }
      onReload();
    } catch {
      toast.error("Failed to update");
    }
  };

  const triggerCelebrate = () => {
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 1400);
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/deliverables/${item.id}`);
      toast.success("Deleted");
      onReload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const completed = !!item.completed;

  return (
    <div
      data-testid={`deliverable-card-${item.id}`}
      className={`group relative overflow-hidden rounded-lg border bg-white p-3 transition-all ${completed ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200 hover:border-zinc-300 hover:-translate-y-[1px] hover:shadow-sm"}`}
      style={!completed ? { borderLeft: `3px solid ${accent}` } : undefined}
    >
      {celebrate && <Celebration />}
      <div className="flex items-center gap-3">
        {!readOnly ? (
          <button
            data-testid={`deliverable-toggle-${item.id}`}
            onClick={() => markComplete(!completed)}
            aria-label={completed ? "Mark incomplete" : "Mark complete"}
            className={`relative flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
              completed
                ? "border-emerald-500 bg-emerald-500"
                : "border-zinc-300 hover:border-[#0066ff] hover:scale-110"
            }`}
          >
            {completed && <Check size={14} className="text-white" strokeWidth={3} />}
          </button>
        ) : (
          <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${completed ? "border-emerald-500 bg-emerald-500" : "border-zinc-300"}`}>
            {completed && <Check size={14} className="text-white" strokeWidth={3} />}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className={`truncate text-sm font-medium ${completed ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
            {item.title}
          </div>
          {item.notes && (
            <div className={`mt-0.5 truncate text-[11px] ${completed ? "text-zinc-400" : "text-zinc-500"}`}>{item.notes}</div>
          )}
        </div>

        {item.counter_enabled && counterTarget > 0 && (
          <div className={`flex items-center gap-1.5 rounded-full border px-1.5 py-0.5 ${counterDone ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
            {!readOnly && (
              <button
                data-testid={`deliverable-${item.id}-decr`}
                onClick={() => setCounter(-1)}
                disabled={counterValue <= 0 || completed}
                className="flex h-6 w-6 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <Minus size={12} />
              </button>
            )}
            <div className={`min-w-[3rem] text-center font-mono text-xs font-semibold ${counterDone ? "text-emerald-700" : "text-zinc-700"}`}>
              {counterValue}<span className="text-zinc-400"> / {counterTarget}</span>
            </div>
            {!readOnly && (
              <button
                data-testid={`deliverable-${item.id}-incr`}
                onClick={() => setCounter(1)}
                disabled={completed}
                className="flex h-6 w-6 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <Plus size={12} />
              </button>
            )}
          </div>
        )}

        {!readOnly && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              data-testid={`deliverable-${item.id}-edit`}
              onClick={onEdit}
              className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
              title="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={remove}
              className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Celebration() {
  // Lightweight burst: a ring of small dots + a star icon scaling/fading.
  const dots = Array.from({ length: 12 });
  return (
    <div data-testid="celebrate-burst" aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
      <div className="relative h-6 w-6">
        <Sparkles size={20} className="absolute inset-0 m-auto animate-[celebrate-pop_1.2s_ease-out_forwards] text-amber-400" />
        {dots.map((_, i) => {
          const angle = (i / dots.length) * Math.PI * 2;
          const x = Math.cos(angle) * 32;
          const y = Math.sin(angle) * 32;
          const colors = ["#0066ff", "#10B981", "#F59E0B", "#EF4444", "#7C3AED", "#EC4899"];
          const color = colors[i % colors.length];
          return (
            <span
              key={i}
              className="absolute left-1/2 top-1/2 block h-1.5 w-1.5 rounded-full"
              style={{
                backgroundColor: color,
                animation: `celebrate-dot 1.1s ease-out forwards`,
                "--dx": `${x}px`,
                "--dy": `${y}px`,
              }}
            />
          );
        })}
      </div>
      <style>{`
        @keyframes celebrate-pop {
          0%   { transform: scale(0.2); opacity: 0; }
          25%  { transform: scale(1.6); opacity: 1; }
          70%  { transform: scale(1.0); opacity: 1; }
          100% { transform: scale(1.0); opacity: 0; }
        }
        @keyframes celebrate-dot {
          0%   { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
          60%  { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(var(--dx), var(--dy)) scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function DeliverableModal({ initial, defaultPeriod, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    period: defaultPeriod || "monthly",
    title: "",
    counter_enabled: false,
    counter_target: 10,
    counter_value: 0,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    const payload = {
      ...form,
      title: form.title.trim(),
      counter_target: form.counter_enabled ? (parseInt(form.counter_target) || 1) : null,
      counter_value: form.counter_enabled ? (parseInt(form.counter_value) || 0) : 0,
    };
    setSaving(true);
    try {
      if (initial) await api.put(`/deliverables/${initial.id}`, payload);
      else await api.post("/deliverables", payload);
      toast.success(initial ? "Updated" : "Goal added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="deliverable-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">{initial ? "Edit Goal" : "New Goal"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Title</span>
            <input
              data-testid="deliverable-form-title"
              autoFocus
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Send 3,000 emails using DeepThree Deliver"
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Period</span>
            <select
              data-testid="deliverable-form-period"
              value={form.period}
              onChange={(e) => set("period", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </label>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-800">
              <input
                data-testid="deliverable-form-counter-toggle"
                type="checkbox"
                checked={!!form.counter_enabled}
                onChange={(e) => set("counter_enabled", e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[#0066ff] focus:ring-[#0066ff]"
              />
              Track with a counter
            </label>
            {form.counter_enabled && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="text-xs font-medium text-zinc-600">
                  Target
                  <input
                    data-testid="deliverable-form-counter-target"
                    type="number"
                    min="1"
                    value={form.counter_target ?? ""}
                    onChange={(e) => set("counter_target", e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
                  />
                </label>
                <label className="text-xs font-medium text-zinc-600">
                  Current
                  <input
                    type="number"
                    min="0"
                    value={form.counter_value ?? 0}
                    onChange={(e) => set("counter_value", e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
                  />
                </label>
              </div>
            )}
          </div>

          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Notes (optional)</span>
            <textarea
              rows={2}
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="deliverable-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
